import {Avatar, Flag, IconButton} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {HTMLAttributes} from 'react';

import {isNil} from 'ramda';

import {UserResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {getUserName} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface TelephonyGroupMemberRowProps extends RequiredTestIdProps {
  user: UserResponseBody | Nullish;
  userId: string;
  position: number;
  isPositionMeaningful: boolean;
  isBeyondSimultaneousCap: boolean;
  isReadOnly: boolean;
  onRemove: (userId: string) => void;
  handleProps?: HTMLAttributes<HTMLDivElement>;
}

const DragHandle = styled.div`
  cursor: grab;
  display: flex;
  touch-action: none;
`;

export function TelephonyGroupMemberRow(props: TelephonyGroupMemberRowProps) {
  const name = getUserName(props.user) ?? props.user?.email ?? props.userId;
  const phoneNumber = props.user?.phoneNumber;
  const hasPhoneNumber = !isNil(phoneNumber);
  const branchName = props.user?.defaultBranch?.name;

  const subtitle = [
    branchName,
    hasPhoneNumber ? `${phoneNumber.prefix} ${phoneNumber.number}` : null,
  ]
    .filter((part) => !isNil(part))
    .join(' · ');

  return (
    <Box
      borderRadius="small"
      border="1px solid"
      borderColor="general.separator"
      backgroundColor="general.white"
      paddingHorizontal={3}
      paddingVertical={2}
      opacity={props.isBeyondSimultaneousCap ? 0.6 : 1}
      data-testid={suffixTestId('row', props)}
    >
      <HStack align="center" spacing={3}>
        <Show when={!props.isReadOnly}>
          <DragHandle {...props.handleProps} data-testid={suffixTestId('dragHandle', props)}>
            <Icon value="action/drag_indicator" color="palettes.neutral.60.100" />
          </DragHandle>
        </Show>
        <Box width={5}>
          <Text
            size="xSmall"
            color={props.isPositionMeaningful ? 'secondary' : 'tertiary'}
            align="center"
            data-testid={suffixTestId('position', props)}
          >
            {props.position}
          </Text>
        </Box>
        <Avatar size="small" name={name} />
        <Box flexGrow={1} minWidth={0}>
          <VStack spacing={0}>
            <Text size="small" alternative data-testid={suffixTestId('name', props)}>
              {name}
            </Text>
            <Show when={subtitle}>
              <Text size="xSmall" color="secondary" data-testid={suffixTestId('subtitle', props)}>
                {subtitle}
              </Text>
            </Show>
          </VStack>
        </Box>
        <Show when={!hasPhoneNumber}>
          <Flag
            size="small"
            colorScheme="orange"
            isSubtle
            label={i18n.t('page.settings.telephony.memberNoPhone')}
            data-testid={suffixTestId('noPhone', props)}
          />
        </Show>
        <Show when={!props.isReadOnly}>
          <IconButton
            icon="navigation/close"
            size="small"
            aria-label={i18n.t('page.settings.telephony.removeMember')}
            onClick={() => props.onRemove(props.userId)}
            data-testid={suffixTestId('remove', props)}
          />
        </Show>
      </HStack>
    </Box>
  );
}
